import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllParties,
  createParty,
  updateParty,
  deleteParty,
  getPartyById,
  clearCurrentParty,
  resetCreate,
  resetUpdate,
} from "../slices/partySlice";
import partyService from "../services/partyService";
import billService from "../services/billService";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";
import BillCreationModal from "../componets/BillCreationModal";
import whatsappApi from "../services/api";
import { generateBillPdf } from "../services/billPdfGenerator";
import businessService from "../services/businessService";
import {
  openPartyCentricBillView,
  downloadPartyCentricBillPdf,
} from "../services/partyCentricBillPdf";
import { toast } from "react-toastify";
import { filterParties, getPartyStateOptions } from "../utils/partySearch";

const PARTY_LIST_LIMIT = 2000;

const PartyManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const {
    parties,
    pagination,
    loading,
    createLoading,
    updateLoading,
    createSuccess,
    updateSuccess,
  } = useSelector((state) => state.party);

  usePermissions();
  const hasLoadedParties = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadParties = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const hasRead = (resource) => {
      const p = userRole.permissions.find((x) => x.resource === resource);
      return p && p.actions.includes("read");
    };
    return hasRead("parties") || hasRead("contacts");
  }, [user?.role, activeRoles]);

  const [searchTerm, setSearchTerm] = useState("");
  const [gstSearchTerm, setGstSearchTerm] = useState("");
  const [gstFilter, setGstFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePartyId, setDeletePartyId] = useState(null);
  const [formData, setFormData] = useState({
    companyName: "",
    partyName: "",
    mobileNumber: "",
    address: "",
    gstin: "",
    aadhar: "",
    pan: "",
    state: "",
    city: "",
    partyType: "Customer",
    isActive: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [editingParty, setEditingParty] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyDetailTab, setPartyDetailTab] = useState("takeBill");
  const [partyBills, setPartyBills] = useState([]);
  const [selectedBillIds, setSelectedBillIds] = useState(new Set());
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentRecording, setPaymentRecording] = useState(false);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billModalPartyId, setBillModalPartyId] = useState(null);
  const [billModalEditBillId, setBillModalEditBillId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [gstLookupLoading, setGstLookupLoading] = useState(false);
  /** If true, GST autofill will not overwrite that field */
  const gstUserEditedRef = useRef({
    companyName: false,
    partyName: false,
    address: false,
  });
  const [billMenuOpen, setBillMenuOpen] = useState(null);
  const [billMenuAnchor, setBillMenuAnchor] = useState(null);
  const [deleteBillModalOpen, setDeleteBillModalOpen] = useState(false);
  const [deleteBillId, setDeleteBillId] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState({ open: false, bill: null, phone: "" });
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close bill menu when clicking outside
  useEffect(() => {
    if (!billMenuOpen) return;
    const handleClick = () => {
      setBillMenuOpen(null);
      setBillMenuAnchor(null);
    };
    const t = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", handleClick);
    };
  }, [billMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadParties) {
      navigate("/my-business");
      return;
    }

    // Only load parties once when component mounts and user is authenticated
    if (!hasLoadedParties.current) {
      dispatch(getAllParties({ page: 1, limit: PARTY_LIST_LIMIT }));
      hasLoadedParties.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadParties]);

  const filteredParties = useMemo(
    () =>
      filterParties(parties, {
        generalSearch: searchTerm,
        gstSearch: gstSearchTerm,
        gstFilter,
        stateFilter,
      }),
    [parties, searchTerm, gstSearchTerm, gstFilter, stateFilter]
  );

  const stateOptions = useMemo(() => getPartyStateOptions(parties), [parties]);

  const reloadPartyList = () => {
    dispatch(getAllParties({ page: 1, limit: PARTY_LIST_LIMIT }));
  };

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setFormData({
        companyName: "",
        partyName: "",
        mobileNumber: "",
        address: "",
        gstin: "",
        aadhar: "",
        pan: "",
        state: "",
        city: "",
        partyType: "Customer",
        isActive: true,
      });
      setValidationErrors({});
      setEditingParty(null);
      dispatch(resetCreate());
      hasLoadedParties.current = false;
      reloadPartyList();
    }
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setFormData({
        companyName: "",
        partyName: "",
        mobileNumber: "",
        address: "",
        gstin: "",
        aadhar: "",
        pan: "",
        state: "",
        city: "",
        partyType: "Customer",
        isActive: true,
      });
      setValidationErrors({});
      setEditingParty(null);
      dispatch(resetUpdate());
      dispatch(clearCurrentParty());
      hasLoadedParties.current = false;
      reloadPartyList();
    }
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (selectedParty) {
      billService.getAllBills({ partyId: selectedParty._id }).then((res) => setPartyBills(res.bills || [])).catch(() => setPartyBills([]));
      setSelectedBillIds(new Set());
    } else {
      setPartyBills([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedParty used intentionally for sync only
  }, [selectedParty?._id]);


  const handleOpenModal = (party = null) => {
    if (party) {
      setEditingParty(party);
      setFormData({
        companyName: party.companyName || "",
        partyName: party.partyName || "",
        mobileNumber: party.mobileNumber || "",
        address: party.address || "",
        gstin: party.gstin || "",
        aadhar: party.aadhar || "",
        pan: party.pan || "",
        state: party.state || "",
        city: party.city || "",
        partyType: party.partyType || "Customer",
        isActive: party.isActive !== undefined ? party.isActive : true,
      });
    } else {
      setEditingParty(null);
      setFormData({
        companyName: "",
        partyName: "",
        mobileNumber: "",
        address: "",
        gstin: "",
        aadhar: "",
        pan: "",
        state: "",
        city: "",
        partyType: "Customer",
        isActive: true,
      });
    }
    setValidationErrors({});
    gstUserEditedRef.current = {
      companyName: false,
      partyName: false,
      address: false,
    };
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      companyName: "",
      partyName: "",
      mobileNumber: "",
      address: "",
      gstin: "",
      aadhar: "",
      pan: "",
      state: "",
      city: "",
      partyType: "Customer",
      isActive: true,
    });
    setValidationErrors({});
    setEditingParty(null);
    gstUserEditedRef.current = {
      companyName: false,
      partyName: false,
      address: false,
    };
    dispatch(clearCurrentParty());
  };

  const validateForm = () => {
    const errors = {};

    if (formData.companyName.trim().length > 200) {
      errors.companyName = "Company name cannot exceed 200 characters";
    }
    if (formData.partyName.trim().length > 200) {
      errors.partyName = "Party name cannot exceed 200 characters";
    }

    if (
      !formData.companyName.trim() &&
      !formData.partyName.trim()
    ) {
      errors.partyName = "Enter company name or party name";
    }

    const mobileDigits = String(formData.mobileNumber || "").replace(/\D/g, "");
    if (mobileDigits.length > 0) {
      if (mobileDigits.length !== 10 || !/^[6-9]\d{9}$/.test(mobileDigits)) {
        errors.mobileNumber = "Please enter a valid 10-digit mobile number";
      }
    }

    // Address validation (optional)
    if (formData.address.trim().length > 500) {
      errors.address = "Address cannot exceed 500 characters";
    }

    // GSTIN validation (optional but must be valid if provided)
    if (formData.gstin && formData.gstin.trim()) {
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinRegex.test(formData.gstin.trim().toUpperCase())) {
        errors.gstin = "Invalid GST Number";
      }
    }

    // Aadhar validation (optional but must be valid if provided)
    if (formData.aadhar && formData.aadhar.trim()) {
      if (!/^\d{12}$/.test(formData.aadhar.trim())) {
        errors.aadhar = "Please enter a valid 12-digit Aadhar number";
      }
    }

    // PAN validation (optional but must be valid if provided)
    if (formData.pan && formData.pan.trim()) {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(formData.pan.trim().toUpperCase())) {
        errors.pan = "Please enter a valid PAN (e.g., ABCDE1234F)";
      }
    }

    // State / City (optional)
    if (formData.state.trim().length > 100) {
      errors.state = "State cannot exceed 100 characters";
    }

    if (formData.city.trim().length > 100) {
      errors.city = "City cannot exceed 100 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const companyTrim = formData.companyName.trim();
    const partyTrim = formData.partyName.trim();
    // Only company → both fields use company; only party → both use party; both filled → send each separately
    let companyNameOut;
    let partyNameOut;
    if (companyTrim && partyTrim) {
      companyNameOut = companyTrim;
      partyNameOut = partyTrim;
    } else if (companyTrim) {
      companyNameOut = companyTrim;
      partyNameOut = companyTrim;
    } else if (partyTrim) {
      companyNameOut = partyTrim;
      partyNameOut = partyTrim;
    } else {
      companyNameOut = "";
      partyNameOut = "";
    }

    const mobileDigits = String(formData.mobileNumber || "").replace(/\D/g, "");
    const partyData = {
      companyName: companyNameOut,
      partyName: partyNameOut,
      mobileNumber: mobileDigits.length === 10 ? mobileDigits : "",
      address: formData.address.trim(),
      gstin: formData.gstin.trim().toUpperCase() || "",
      aadhar: formData.aadhar.trim() || "",
      pan: formData.pan.trim().toUpperCase() || "",
      state: formData.state.trim(),
      city: formData.city.trim(),
      partyType: formData.partyType || "Customer",
      isActive: formData.isActive,
    };

    if (editingParty) {
      dispatch(updateParty({ partyId: editingParty._id, partyData }));
    } else {
      dispatch(createParty(partyData));
    }
  };

  /**
   * On blur: valid 15-char GST → GET /api/parties/gst-lookup (server uses GST_LOOKUP_API_KEY + GSTINCheck).
   * Avoids wrong Masters India URL/auth and browser CORS issues. Mobile is never overwritten.
   */
  const handleGstinLookup = async () => {
    const gstin = (formData.gstin || "").trim().toUpperCase();
    if (gstin.length !== 15) {
      setValidationErrors((prev) => ({ ...prev, gstin: "" }));
      return;
    }
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinRegex.test(gstin)) {
      setValidationErrors((prev) => ({
        ...prev,
        gstin: "Invalid GST Number",
      }));
      return;
    }

    setGstLookupLoading(true);
    setValidationErrors((prev) => ({ ...prev, gstin: "" }));
    try {
      const res = await partyService.getGstLookup(gstin);
      const name = (res.tradeName || res.legalName || "").trim();
      const addr = (res.address || "").trim();
      setFormData((prev) => ({
        ...prev,
        gstin,
        companyName: gstUserEditedRef.current.companyName
          ? prev.companyName
          : name || prev.companyName,
        partyName: gstUserEditedRef.current.partyName
          ? prev.partyName
          : name || prev.partyName,
        address: gstUserEditedRef.current.address
          ? prev.address
          : addr || prev.address,
        pan: res.pan || prev.pan,
        state: res.state || prev.state,
        city: res.city || prev.city,
      }));
      if (!name && !addr) {
        toast.info(
          "GST verified but no name or address was returned. If details stay empty, renew GSTINCheck credits (backend GST_LOOKUP_API_KEY) or try again later."
        );
      }
    } catch (err) {
      const raw =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to fetch GST details";
      const lower = String(raw).toLowerCase();
      const isInvalid =
        lower.includes("invalid") && lower.includes("gstin");
      const isForbidden =
        err?.status === 403 || lower.includes("permission");
      setValidationErrors((prev) => ({
        ...prev,
        gstin: isInvalid
          ? "Invalid GST Number"
          : isForbidden
            ? "You do not have permission to look up GST."
            : "Unable to fetch GST details",
      }));
    } finally {
      setGstLookupLoading(false);
    }
  };

  const handleOpenDeleteModal = (partyId) => {
    setDeletePartyId(partyId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeletePartyId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (deletePartyId) {
      dispatch(deleteParty(deletePartyId));
      setIsDeleteModalOpen(false);
      setDeletePartyId(null);
      hasLoadedParties.current = false;
      setTimeout(() => {
        reloadPartyList();
        hasLoadedParties.current = true;
      }, 500);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _handleView = async (partyId) => {
    await dispatch(getPartyById(partyId));
    const party = parties.find((p) => p._id === partyId);
    if (party) {
      handleOpenModal(party);
    }
  };

  // When selected bills change, set payment amount to total due (for partial payment user can edit). Must be before any early return.
  const pendingBillsForPayment = useMemo(() => partyBills.filter((b) => b.status === "pending" && selectedBillIds.has(b._id)), [partyBills, selectedBillIds]);
  const totalDueSelected = useMemo(() => pendingBillsForPayment.reduce((sum, b) => sum + ((b.grandTotal || 0) - (b.paidAmount || 0)), 0), [pendingBillsForPayment]);
  useEffect(() => {
    if (selectedBillIds.size > 0 && totalDueSelected >= 0) {
      setPaymentAmount(String(totalDueSelected.toFixed(2)));
    } else {
      setPaymentAmount("");
    }
  }, [selectedBillIds.size, totalDueSelected]);

  if (loading && parties.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  const handleSavePartyType = (newType) => {
    if (!selectedParty) return;
    dispatch(updateParty({ partyId: selectedParty._id, partyData: { partyType: newType } }));
    setSelectedParty((p) => (p ? { ...p, partyType: newType } : null));
    toast.success("Party type updated");
  };

  const handleRecordPayment = async () => {
    if (selectedBillIds.size === 0) {
      toast.info("Select at least one bill for payment");
      return;
    }
    const amountNum = parseFloat(paymentAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    const selectedBills = partyBills.filter((b) => b.status === "pending" && selectedBillIds.has(b._id));
    if (selectedBills.length === 0) {
      toast.info("Select at least one pending bill");
      return;
    }
    const totalDue = selectedBills.reduce((sum, b) => sum + ((b.grandTotal || 0) - (b.paidAmount || 0)), 0);
    if (amountNum > totalDue) {
      toast.error(`Amount cannot exceed total due ₹${totalDue.toFixed(2)}`);
      return;
    }
    setPaymentRecording(true);
    try {
      let remaining = amountNum;
      for (const bill of selectedBills) {
        if (remaining <= 0) break;
        const balance = (bill.grandTotal || 0) - (bill.paidAmount || 0);
        const payThis = Math.min(remaining, balance);
        if (payThis <= 0) continue;
        const newPaidAmount = (bill.paidAmount || 0) + payThis;
        const updateData = { paidAmount: newPaidAmount };
        if (newPaidAmount >= (bill.grandTotal || 0)) updateData.status = "paid";
        await billService.updateBill(bill._id, updateData);
        remaining -= payThis;
      }
      const msg = amountNum >= totalDue
        ? `Complete Paid – ₹${amountNum.toFixed(2)} for ${selectedBills.length} bill(s)`
        : `Partial payment – ₹${amountNum.toFixed(2)} recorded`;
      toast.success(msg);
      setSelectedBillIds(new Set());
      setPaymentAmount("");
      billService.getAllBills({ partyId: selectedParty._id }).then((res) => setPartyBills(res.bills || [])).catch(() => setPartyBills([]));
      reloadPartyList();
    } catch (err) {
      const msg = typeof err === "object" && err?.message ? err.message : "Failed to record payment";
      toast.error(msg);
    } finally {
      setPaymentRecording(false);
    }
  };

  const toggleBillSelection = (billId) => {
    setSelectedBillIds((prev) => {
      const next = new Set(prev);
      if (next.has(billId)) next.delete(billId);
      else next.add(billId);
      return next;
    });
  };

  // Bill actions: 3-dot menu handlers
  const closeBillMenu = () => setBillMenuOpen(null);

  const handleBillEdit = (bill) => {
    closeBillMenu();
    setBillModalEditBillId(bill._id);
    setBillModalPartyId(selectedParty._id);
    setBillModalOpen(true);
  };

  const getBillPdfBlob = async (billId) => {
    try {
      return await billService.getBillPdfBlob(billId);
    } catch (err) {
      const is404 = err?.status === 404 || err?.response?.status === 404 || /not found/i.test(String(err?.message || ""));
      if (is404) {
        toast.info("Generating PDF...");
        await generateBillPdf(billId);
        return await billService.getBillPdfBlob(billId);
      }
      throw err;
    }
  };

  const handleBillView = async (bill) => {
    closeBillMenu();
    try {
      const blob = await getBillPdfBlob(bill._id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err?.message || "Failed to load PDF");
    }
  };

  const handleBillDownload = async (bill) => {
    closeBillMenu();
    try {
      const blob = await getBillPdfBlob(bill._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(bill.billNumber || "bill").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error(err?.message || "Failed to download PDF");
    }
  };

  const mergePartyOntoBill = (fullBill) => {
    if (!fullBill || !selectedParty?._id) return fullBill;
    const pid = fullBill.partyId?._id || fullBill.partyId;
    const matches =
      pid === selectedParty._id ||
      fullBill.party?._id === selectedParty._id;
    if (matches && !fullBill.party) {
      return { ...fullBill, party: selectedParty };
    }
    return fullBill;
  };

  const handlePartyBillView = async (bill) => {
    closeBillMenu();
    try {
      const res = await billService.getBillById(bill._id);
      let fullBill = res?.bill ?? res;
      if (!fullBill?._id) throw new Error("Bill not found");
      if (!(fullBill.items || []).length) throw new Error("Bill has no items");
      fullBill = mergePartyOntoBill(fullBill);
      let business = null;
      try {
        const br = await businessService.getMyBusiness();
        business = br?.business ?? null;
      } catch (e) {
        console.warn("Business fetch failed:", e);
      }
      await openPartyCentricBillView(fullBill, business);
    } catch (err) {
      toast.error(err?.message || "Failed to open party view");
    }
  };

  const handlePartyBillDownload = async (bill) => {
    closeBillMenu();
    try {
      const res = await billService.getBillById(bill._id);
      let fullBill = res?.bill ?? res;
      if (!fullBill?._id) throw new Error("Bill not found");
      if (!(fullBill.items || []).length) throw new Error("Bill has no items");
      fullBill = mergePartyOntoBill(fullBill);
      let business = null;
      try {
        const br = await businessService.getMyBusiness();
        business = br?.business ?? null;
      } catch (e) {
        console.warn("Business fetch failed:", e);
      }
      await downloadPartyCentricBillPdf(fullBill, business);
      toast.success("Party layout download started");
    } catch (err) {
      toast.error(err?.message || "Failed to download party PDF");
    }
  };

  const handleBillPrint = async (bill) => {
    closeBillMenu();
    try {
      const blob = await getBillPdfBlob(bill._id);
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!printWindow) {
        toast.error("Please allow popups to print. Or use View then Ctrl+P.");
        URL.revokeObjectURL(url);
        return;
      }
      toast.info("Opening print dialog...");
      let revoked = false;
      const doPrint = () => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = () => { try { printWindow.close(); } catch (e) {} };
          }
        } catch (e) {
          if (printWindow && !printWindow.closed) printWindow.close();
        }
        if (!revoked) {
          revoked = true;
          URL.revokeObjectURL(url);
        }
      };
      printWindow.onload = () => setTimeout(doPrint, 800);
      // Fallback: PDF viewers may not fire onload, so trigger print after delay
      setTimeout(() => {
        if (printWindow && !printWindow.closed) doPrint();
        else if (!revoked) {
          revoked = true;
          URL.revokeObjectURL(url);
        }
      }, 2000);
    } catch (err) {
      toast.error(err?.message || "Failed to print PDF");
    }
  };

  const handleBillDeleteClick = (bill) => {
    closeBillMenu();
    setDeleteBillId(bill._id);
    setDeleteBillModalOpen(true);
  };

  const handleBillDeleteConfirm = async () => {
    if (!deleteBillId) return;
    try {
      await billService.deleteBill(deleteBillId);
      toast.success("Bill deleted");
      setDeleteBillModalOpen(false);
      setDeleteBillId(null);
      if (selectedParty?._id) billService.getAllBills({ partyId: selectedParty._id }).then((res) => setPartyBills(res.bills || [])).catch(() => setPartyBills([]));
    } catch (err) {
      toast.error(err?.message || "Failed to delete bill");
    }
  };

  const openWhatsappModal = (bill) => {
    closeBillMenu();
    const phone = (selectedParty?.mobileNumber || bill.party?.mobileNumber || "").replace(/\D/g, "").slice(-10);
    setWhatsappModal({ open: true, bill, phone });
  };

  const closeWhatsappModal = () => setWhatsappModal({ open: false, bill: null, phone: "" });

  const handleSendWhatsAppBill = async () => {
    const { bill, phone } = whatsappModal;
    if (!bill?._id) return;
    const num = (phone || "").replace(/\D/g, "").slice(-10);
    if (num.length !== 10) {
      toast.error("Enter a valid 10-digit phone number.");
      return;
    }
    setSendingWhatsApp(true);
    try {
      let full = await billService.getBillById(bill._id);
      let fullBill = full?.bill ?? full;
      let filePath = fullBill?.pdfFile?.fileUrl;
      const partyLabelRaw =
        selectedParty?.companyName ||
        selectedParty?.partyName ||
        fullBill?.party?.companyName ||
        fullBill?.party?.partyName ||
        "Customer";
      const safePartyLabel = String(partyLabelRaw)
        .trim()
        .replace(/[\\/:*?"<>|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);
      const desiredFileName = `${safePartyLabel || "Customer"}.pdf`;

      if (!filePath) {
        toast.info("Generating PDF...");
        await generateBillPdf(bill._id);
        full = await billService.getBillById(bill._id);
        fullBill = full?.bill ?? full;
        filePath = fullBill?.pdfFile?.fileUrl;
      }

      if (!filePath) {
        toast.error("Could not generate PDF. Please try again.");
        return;
      }

      await whatsappApi.post("/wp/send", {
        number: num,
        filePath: filePath.replace(/^\//, ""),
        message: bill.billNumber || "",
        fileName: desiredFileName,
      });
      toast.success("Bill sent to WhatsApp.");
      closeWhatsappModal();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to send to WhatsApp.";
      toast.error(msg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        flexDirection: isMobile && selectedParty ? "column" : "row",
      }}>
        {/* Left: Party list */}
        <div style={{
          ...styles.content,
          padding: isMobile ? "16px" : "24px",
          paddingBottom: isMobile ? "100px" : "100px",
          flex: selectedParty && !isMobile ? "0 0 320px" : 1,
          minWidth: 0,
          borderRight: selectedParty && !isMobile ? "1px solid #e9edef" : "none",
        }}>
        {/* Page Header */}
        <div style={{
          ...styles.pageHeader,
          marginBottom: isMobile ? "16px" : "24px",
        }}>
          <h1 style={{
            ...styles.pageTitle,
            fontSize: isMobile ? "28px" : "32px",
          }}>Customer List</h1>
        </div>

        {/* Search & filters */}
        <div style={{
          ...styles.searchContainer,
          marginBottom: isMobile ? "16px" : "20px",
          padding: isMobile ? "8px" : "12px",
        }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                placeholder="Name, mobile, address (English / Gujarati)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...styles.searchInput, paddingLeft: 12 }}
              />
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                placeholder="GST number search..."
                value={gstSearchTerm}
                onChange={(e) => setGstSearchTerm(e.target.value.toUpperCase())}
                style={{ ...styles.searchInput, paddingLeft: 12 }}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "#667781", fontWeight: 500 }}>GST:</span>
            {[
              { id: "all", label: "All" },
              { id: "gst", label: "GST" },
              { id: "nonGst", label: "Non GST" },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setGstFilter(id)}
                style={{
                  ...styles.filterChip,
                  ...(gstFilter === id ? styles.filterChipActive : {}),
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <label style={{ fontSize: 12, color: "#667781", fontWeight: 500, whiteSpace: "nowrap" }}>State:</label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{ ...styles.stateSelect, maxWidth: isMobile ? "100%" : 220, width: isMobile ? "100%" : undefined }}
            >
              <option value="">All states</option>
              {stateOptions.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            {(searchTerm || gstSearchTerm || gstFilter !== "all" || stateFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setGstSearchTerm("");
                  setGstFilter("all");
                  setStateFilter("");
                }}
                style={styles.clearFiltersBtn}
              >
                Clear filters
              </button>
            )}
            <span style={{ fontSize: 12, color: "#667781", marginLeft: "auto" }}>
              {filteredParties.length} of {parties.length}
            </span>
          </div>
        </div>

        {/* Party Cards */}
        <div style={{
          ...styles.partiesContainer,
          gap: isMobile ? "12px" : "16px",
        }}>
          {filteredParties.length === 0 ? (
            <div style={{
              ...styles.noParties,
              padding: isMobile ? "60px 20px" : "80px 40px",
            }}>
              <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>👥</div>
              <p style={{ margin: "0 0 24px 0", fontSize: "17px", fontWeight: "400", color: "#667781" }}>
                {parties.length === 0 ? "No customers yet" : "No customers match your search"}
              </p>
              {parties.length === 0 && (
              <PermissionGate resource="parties" action="create">
                <button
                  onClick={() => handleOpenModal()}
                  style={styles.createFirstButton}
                >
                  Create First Customer
                </button>
              </PermissionGate>
              )}
            </div>
          ) : (
            filteredParties.map((party) => (
              <div 
                key={party._id} 
                role="button"
                tabIndex={0}
                onClick={() => setSelectedParty(party)}
                onKeyDown={(e) => e.key === "Enter" && setSelectedParty(party)}
                style={{
                  ...styles.partyCard,
                  padding: isMobile ? "16px" : "20px",
                  cursor: "pointer",
                  borderLeft: selectedParty?._id === party._id ? "4px solid #25d366" : undefined,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                <div style={styles.partyCardContent}>
                  <div style={styles.partyInfo}>
                    <div style={{
                      ...styles.partyName,
                      fontSize: isMobile ? "16px" : "18px",
                    }}>
                      {party.partyName.toUpperCase()}
                    </div>
                    <div style={{ display: "flex", gap: "12px", marginTop: "6px", fontSize: "13px", fontWeight: 500 }}>
                      <span style={{ color: "#dc3545" }}>Due: ₹{(party.totalDue ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                      <span style={{ color: "#25d366" }}>Paid: ₹{(party.totalPaid ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={styles.partyDetails}>
                      {(party.state || party.city) && (
                        <div style={styles.partyDetailRow}>
                          <span style={styles.partyDetailLabel}>State: </span>
                          <span style={styles.partyDetailValue}>{party.state || "-"}</span>
                          {party.state && party.city && <span style={styles.separator}> | </span>}
                          <span style={styles.partyDetailLabel}>City: </span>
                          <span style={styles.partyDetailValue}>{party.city || "-"}</span>
                        </div>
                      )}
                      {party.address && (
                        <div style={styles.partyDetailRow}>
                          <span style={styles.partyDetailLabel}>Address: </span>
                          <span style={styles.partyDetailValue}>{party.address}</span>
                        </div>
                      )}
                      {party.gstin && (
                        <div style={styles.partyDetailRow}>
                          <span style={styles.partyDetailLabel}>GSTIN: </span>
                          <span style={styles.partyDetailValue}>{party.gstin}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={styles.partyActions} onClick={(e) => e.stopPropagation()}>
                    <PermissionGate resource="parties" action="update">
                      <button
                        onClick={() => handleOpenModal(party)}
                        style={styles.partyEditButton}
                        title="Edit Customer"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f2f5";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        ✏️
                      </button>
                    </PermissionGate>
                    <PermissionGate resource="parties" action="delete">
                      <button
                        onClick={() => handleOpenDeleteModal(party._id)}
                        style={styles.partyDeleteButton}
                        title="Delete Customer"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff5f5";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        🗑️
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              onClick={() =>
                dispatch(
                  getAllParties({
                    page: pagination.currentPage - 1,
                    limit: 100,
                    search: searchTerm,
                  })
                )
              }
              disabled={pagination.currentPage === 1}
              style={styles.pageButton}
            >
              Previous
            </button>
            <span style={styles.pageInfo}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                dispatch(
                  getAllParties({
                    page: pagination.currentPage + 1,
                    limit: 100,
                    search: searchTerm,
                  })
                )
              }
              disabled={pagination.currentPage === pagination.totalPages}
              style={styles.pageButton}
            >
              Next
            </button>
          </div>
        )}

        </div>

        {/* Mobile: backdrop when party detail is open */}
        {selectedParty && isMobile && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedParty(null)}
            onKeyDown={(e) => e.key === "Escape" && setSelectedParty(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 1001,
            }}
            aria-label="Close"
          />
        )}
        {/* Right: Party detail - responsive overlay (mobile) or side panel (desktop) */}
        {selectedParty && (
          <div
            style={{
              position: isMobile ? "fixed" : "relative",
              top: isMobile ? 0 : undefined,
              left: isMobile ? 0 : undefined,
              right: isMobile ? 0 : undefined,
              bottom: isMobile ? 0 : undefined,
              zIndex: isMobile ? 1002 : 0,
              flex: isMobile ? "none" : 1,
              minWidth: isMobile ? "100%" : 0,
              width: isMobile ? "100%" : undefined,
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              borderLeft: isMobile ? "none" : "1px solid #e9edef",
              boxShadow: isMobile ? "0 -4px 20px rgba(0,0,0,0.15)" : "none",
              borderRadius: isMobile ? "16px 16px 0 0" : 0,
              overflow: "hidden",
            }}
          >
            <div style={{ padding: isMobile ? "16px 20px 16px 20px" : "16px 20px", borderBottom: "1px solid #e9edef", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? "20px" : "18px", fontWeight: 600, color: "#111b21" }}>{selectedParty.partyName}</h2>
              <button type="button" onClick={() => setSelectedParty(null)} style={{ padding: isMobile ? "10px 16px" : "8px 12px", border: "none", background: "#f0f2f5", borderRadius: "8px", cursor: "pointer", fontSize: isMobile ? "16px" : "14px" }}>✕ Close</button>
            </div>
            {/* Take Bill, Take Payment, Party Type - same style as Add Party button */}
            <div style={{
              display: "flex",
              gap: isMobile ? "12px" : "10px",
              padding: isMobile ? "16px 20px" : "12px 20px",
              borderBottom: "1px solid #e9edef",
              flexWrap: "wrap",
            }}>
              <button
                type="button"
                onClick={() => setPartyDetailTab("takeBill")}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "120px" : "100px",
                  padding: isMobile ? "14px 20px" : "12px 18px",
                  border: "none",
                  borderRadius: isMobile ? "28px" : "24px",
                  background: partyDetailTab === "takeBill" ? "#dc3545" : "#ffeef2",
                  color: partyDetailTab === "takeBill" ? "#fff" : "#dc3545",
                  fontWeight: 600,
                  fontSize: isMobile ? "15px" : "13px",
                  cursor: "pointer",
                  boxShadow: partyDetailTab === "takeBill" ? "0 2px 8px rgba(220, 53, 69, 0.3)" : "0 2px 6px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <span>🧾</span> Take Bill
              </button>
              <button
                type="button"
                onClick={() => setPartyDetailTab("takePayment")}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "120px" : "100px",
                  padding: isMobile ? "14px 20px" : "12px 18px",
                  border: "none",
                  borderRadius: isMobile ? "28px" : "24px",
                  background: partyDetailTab === "takePayment" ? "#dc3545" : "#ffeef2",
                  color: partyDetailTab === "takePayment" ? "#fff" : "#dc3545",
                  fontWeight: 600,
                  fontSize: isMobile ? "15px" : "13px",
                  cursor: "pointer",
                  boxShadow: partyDetailTab === "takePayment" ? "0 2px 8px rgba(220, 53, 69, 0.3)" : "0 2px 6px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <span>💰</span> Take Payment
              </button>
              <button
                type="button"
                onClick={() => setPartyDetailTab("partyType")}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "140px" : "120px",
                  padding: isMobile ? "14px 20px" : "12px 18px",
                  border: "none",
                  borderRadius: isMobile ? "28px" : "24px",
                  background: partyDetailTab === "partyType" ? "#dc3545" : "#ffeef2",
                  color: partyDetailTab === "partyType" ? "#fff" : "#dc3545",
                  fontWeight: 600,
                  fontSize: isMobile ? "16px" : "14px",
                  cursor: "pointer",
                  boxShadow: partyDetailTab === "partyType" ? "0 2px 8px rgba(220, 53, 69, 0.3)" : "0 2px 6px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <span>👤</span> Party Type
              </button>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: isMobile ? "20px 20px 100px 20px" : "20px" }}>
              {partyDetailTab === "takeBill" && (
                <div>
                  <p style={{ marginBottom: "12px", fontSize: "14px", color: "#667781" }}>Create a new bill for this party.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setBillModalEditBillId(null);
                      setBillModalPartyId(selectedParty._id);
                      setBillModalOpen(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      width: isMobile ? "100%" : "auto",
                      minWidth: isMobile ? undefined : "140px",
                      padding: isMobile ? "14px 24px" : "12px 24px",
                      background: "#dc3545",
                      color: "#fff",
                      border: "none",
                      borderRadius: isMobile ? "28px" : "12px",
                      fontWeight: 600,
                      fontSize: isMobile ? "16px" : "15px",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
                    }}
                  >
                    <span>🧾</span> Sell
                  </button>
                  <p style={{ marginTop: "12px", marginBottom: "20px", fontSize: "13px", color: "#667781" }}>Opens bill form for this party on this page.</p>

                  {/* This party's bills list */}
                  <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e9edef" }}>
                    <p style={{ fontSize: "14px", fontWeight: 600, color: "#111b21", marginBottom: "12px" }}>This party's bills</p>
                    {partyBills.length === 0 ? (
                      <p style={{ fontSize: "13px", color: "#667781" }}>No bills yet. Create one using Sell above.</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "280px", overflowY: "auto" }}>
                        {partyBills.map((bill) => {
                          const due = (bill.grandTotal || 0) - (bill.paidAmount || 0);
                          const isPaid = bill.status === "paid";
                          const menuOpen = billMenuOpen === bill._id;
                          return (
                            <div
                              key={bill._id}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                if (menuOpen) return;
                                setBillModalEditBillId(bill._id);
                                setBillModalPartyId(selectedParty._id);
                                setBillModalOpen(true);
                              }}
                              onKeyDown={(e) => e.key === "Enter" && (setBillModalEditBillId(bill._id), setBillModalPartyId(selectedParty._id), setBillModalOpen(true))}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                padding: "10px 12px",
                                background: isPaid ? "#e7f8ee" : "#f7f8fa",
                                borderRadius: "8px",
                                borderLeft: `3px solid ${isPaid ? "#25d366" : "#dc3545"}`,
                                cursor: "pointer",
                                position: "relative",
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontWeight: 600, fontSize: "14px", color: "#111b21" }}>{bill.billNumber || "—"}</span>
                                {bill.billDate && (
                                  <span style={{ fontSize: "12px", color: "#667781", marginLeft: "8px" }}>
                                    {new Date(bill.billDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                                <span style={{ fontWeight: 600, fontSize: "14px", color: "#111b21" }}>₹{(bill.grandTotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                                {!isPaid && due < (bill.grandTotal || 0) && (
                                  <span style={{ fontSize: "12px", color: "#dc3545", fontWeight: 500 }}>Due ₹{due.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                                )}
                                <span style={{
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  background: isPaid ? "#25d366" : "#dc3545",
                                  color: "#fff",
                                }}>
                                  {isPaid ? "Paid" : "Pending"}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (menuOpen) {
                                      setBillMenuOpen(null);
                                      setBillMenuAnchor(null);
                                      return;
                                    }

                                    // Render dropdown outside the scroll container (via portal),
                                    // so it doesn't get clipped by overflow: auto.
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const menuWidth = 200; // should match minWidth in the menu below
                                    const padding = 8;
                                    const left = Math.max(padding, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - padding));
                                    setBillMenuAnchor({
                                      top: rect.bottom + window.scrollY,
                                      left: left + window.scrollX,
                                    });
                                    setBillMenuOpen(bill._id);
                                  }}
                                  style={{
                                    padding: "4px 6px",
                                    background: "transparent",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    color: "#667781",
                                    fontSize: "18px",
                                    lineHeight: 1,
                                  }}
                                  title="More options"
                                  aria-label="More options"
                                >
                                  ⋮
                                </button>
                              </div>
                              {menuOpen && (
                                billMenuAnchor ? (
                                  ReactDOM.createPortal(
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: billMenuAnchor.top,
                                        left: billMenuAnchor.left,
                                        background: "#fff",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        border: "1px solid #e9edef",
                                        minWidth: "180px",
                                        zIndex: 10000,
                                        overflow: "hidden",
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button type="button" onClick={() => handleBillEdit(bill)} style={billMenuBtnStyle}>
                                        ✏️ Edit
                                      </button>
                                      <button type="button" onClick={() => handleBillView(bill)} style={billMenuBtnStyle}>
                                        👁️ View
                                      </button>
                                      <button type="button" onClick={() => handleBillDownload(bill)} style={billMenuBtnStyle}>
                                        ⬇️ Download
                                      </button>
                                      <button type="button" onClick={() => handlePartyBillView(bill)} style={billMenuBtnStyle}>
                                        👤 Party view
                                      </button>
                                      <button type="button" onClick={() => handlePartyBillDownload(bill)} style={billMenuBtnStyle}>
                                        ⬇️ Party download
                                      </button>
                                      <button type="button" onClick={() => handleBillPrint(bill)} style={billMenuBtnStyle}>
                                        🖨️ Print
                                      </button>
                                      <button type="button" onClick={() => openWhatsappModal(bill)} style={billMenuBtnStyle}>
                                        📱 Send WhatsApp
                                      </button>
                                      <button type="button" onClick={() => handleBillDeleteClick(bill)} style={{ ...billMenuBtnStyle, color: "#dc3545" }}>
                                        🗑️ Delete
                                      </button>
                                    </div>,
                                    document.body
                                  )
                                ) : null
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {partyDetailTab === "partyType" && (
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>Party type (Vyapari / Customer / Supplier)</label>
                  <select value={selectedParty.partyType || "Customer"} onChange={(e) => handleSavePartyType(e.target.value)} style={{ padding: "10px 12px", borderRadius: "8px", border: "1px solid #e9edef", minWidth: "200px" }}>
                    <option value="Vyapari">Vyapari</option>
                    <option value="Customer">Customer</option>
                    <option value="Supplier">Supplier</option>
                    <option value="Other">Other</option>
                  </select>
                  <p style={{ marginTop: "12px", fontSize: "13px", color: "#667781" }}>Select type and it saves automatically.</p>
                </div>
              )}
              {partyDetailTab === "takePayment" && (
                <div>
                  <p style={{ marginBottom: "12px", fontSize: "14px", color: "#667781" }}>Select bill(s), enter amount (defaults to total due). Partial payment supported.</p>
                  {partyBills.length === 0 ? (
                    <p style={{ color: "#667781" }}>No bills for this party.</p>
                  ) : (
                    (() => {
                      const pendingBills = partyBills.filter((b) => b.status === "pending");
                      const paidBills = partyBills.filter((b) => b.status === "paid");
                      const totalDueBalance = pendingBills.reduce((sum, b) => sum + ((b.grandTotal || 0) - (b.paidAmount || 0)), 0);
                      return (
                        <>
                          <div style={{ marginBottom: "16px", padding: "12px", background: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107" }}>
                            <span style={{ fontSize: "13px", color: "#856404", fontWeight: 600 }}>Total due balance: </span>
                            <span style={{ fontSize: "18px", fontWeight: 700, color: "#111b21" }}>₹{totalDueBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#856404" }}>Outstanding across all {pendingBills.length} pending bill(s). After payment, balance will reduce.</p>
                          </div>
                          <div style={{ marginBottom: "16px" }}>
                            {pendingBills.length > 0 && (
                              <>
                                <p style={{ fontSize: "12px", color: "#667781", marginBottom: "8px", fontWeight: 600 }}>PENDING</p>
                                {pendingBills.map((bill) => {
                                  const due = (bill.grandTotal || 0) - (bill.paidAmount || 0);
                                  return (
                                    <label key={bill._id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px", background: selectedBillIds.has(bill._id) ? "#e7f8ee" : "#f7f8fa", borderRadius: "8px", marginBottom: "8px", cursor: "pointer" }}>
                                      <input type="checkbox" checked={selectedBillIds.has(bill._id)} onChange={() => toggleBillSelection(bill._id)} disabled={paymentRecording} />
                                      <span style={{ flex: 1 }}>{bill.billNumber}</span>
                                      <span style={{ fontWeight: 600 }}>₹{(bill.grandTotal || 0).toFixed(2)}{due < (bill.grandTotal || 0) ? ` (due ₹${due.toFixed(2)})` : ""}</span>
                                    </label>
                                  );
                                })}
                              </>
                            )}
                            {paidBills.length > 0 && (
                              <>
                                <p style={{ fontSize: "12px", color: "#25d366", marginBottom: "8px", marginTop: pendingBills.length > 0 ? "16px" : 0, fontWeight: 600 }}>PAID</p>
                                {paidBills.map((bill) => (
                                  <div key={bill._id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px", background: "#e7f8ee", borderRadius: "8px", marginBottom: "8px", opacity: 0.9 }}>
                                    <span style={{ width: "20px" }}>✓</span>
                                    <span style={{ flex: 1 }}>{bill.billNumber}</span>
                                    <span style={{ fontWeight: 600 }}>₹{(bill.grandTotal || 0).toFixed(2)}</span>
                                    <span style={{ fontSize: "11px", background: "#25d366", color: "#fff", padding: "2px 8px", borderRadius: "4px", fontWeight: 500 }}>Paid</span>
                                  </div>
                                ))}
                              </>
                            )}
                            {pendingBills.length === 0 && paidBills.length === 0 && (
                              <p style={{ color: "#667781" }}>No bills for this party.</p>
                            )}
                          </div>
                          {selectedBillIds.size > 0 && (
                            <>
                              <div style={{ marginBottom: "8px", fontSize: "13px", color: "#667781" }}>
                                Total due (selected): ₹{totalDueSelected.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </div>
                              <div style={{ marginBottom: "16px" }}>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#111b21", marginBottom: "6px" }}>Amount (₹)</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={paymentAmount}
                                  onChange={(e) => setPaymentAmount(e.target.value)}
                                  disabled={paymentRecording}
                                  placeholder={totalDueSelected.toFixed(2)}
                                  style={{
                                    width: "100%",
                                    maxWidth: "200px",
                                    padding: "10px 12px",
                                    fontSize: "16px",
                                    border: "1px solid #e9edef",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                  }}
                                />
                                <p style={{ fontSize: "11px", color: "#667781", marginTop: "4px" }}>Enter full or partial amount to record payment.</p>
                              </div>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={handleRecordPayment}
                            disabled={selectedBillIds.size === 0 || paymentRecording || !paymentAmount || parseFloat(paymentAmount) <= 0}
                            style={{
                              padding: "10px 20px",
                              background: selectedBillIds.size > 0 && !paymentRecording && paymentAmount && parseFloat(paymentAmount) > 0 ? "#25d366" : "#a0a0a0",
                              color: "#fff",
                              border: "none",
                              borderRadius: "8px",
                              fontWeight: 500,
                              cursor: selectedBillIds.size > 0 && !paymentRecording && paymentAmount && parseFloat(paymentAmount) > 0 ? "pointer" : "not-allowed",
                            }}
                          >
                            {paymentRecording ? "Recording…" : "Record Payment"}
                          </button>
                        </>
                      );
                    })()
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Action Button - When party selected: Sell. When no party selected: Add Party */}
      {selectedParty ? (
        <button
          onClick={() => {
            setBillModalEditBillId(null);
            setBillModalPartyId(selectedParty._id);
            setBillModalOpen(true);
          }}
          style={{
            ...styles.fabButton,
            bottom: isMobile ? "90px" : "100px",
            borderRadius: isMobile ? "28px" : "32px",
            padding: isMobile ? "14px 24px" : "16px 32px",
            fontSize: isMobile ? "16px" : "18px",
            zIndex: 1001,
          }}
          title="Create bill for this party"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(220, 53, 69, 0.4)";
          }}
        >
          <span style={{ ...styles.fabIcon, fontSize: isMobile ? "20px" : "24px" }}>🧾</span>
          <span style={styles.fabText}>Sell</span>
        </button>
      ) : (
        <PermissionGate resource="parties" action="create">
          <button
            onClick={() => handleOpenModal()}
            style={{
              ...styles.fabButton,
              bottom: isMobile ? "90px" : "100px",
              borderRadius: isMobile ? "28px" : "32px",
              padding: isMobile ? "14px 24px" : "16px 32px",
              fontSize: isMobile ? "16px" : "18px",
              zIndex: 1001,
            }}
            title="Add Party"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateX(-50%) translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateX(-50%) translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(220, 53, 69, 0.4)";
            }}
          >
            <span style={{ ...styles.fabIcon, fontSize: isMobile ? "20px" : "24px" }}>👥</span>
            <span style={styles.fabText}>Add Party</span>
          </button>
        </PermissionGate>
      )}

      {/* Delete Bill Confirmation Modal */}
      {deleteBillModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => { setDeleteBillModalOpen(false); setDeleteBillId(null); }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "360px",
              width: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: "0 0 20px 0", fontSize: "16px", color: "#111b21" }}>Are you sure you want to delete this bill?</p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => { setDeleteBillModalOpen(false); setDeleteBillId(null); }} style={{ padding: "10px 20px", border: "1px solid #ddd", borderRadius: "8px", background: "#fff", cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={handleBillDeleteConfirm} style={{ padding: "10px 20px", border: "none", borderRadius: "8px", background: "#dc3545", color: "#fff", cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Send to WhatsApp Modal */}
      {whatsappModal.open && whatsappModal.bill && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={closeWhatsappModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "360px",
              width: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 12px 0", fontSize: "18px", fontWeight: 600 }}>Send bill to WhatsApp</h2>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#667781" }}>{whatsappModal.bill.billNumber} will be sent as PDF.</p>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>Phone (10 digits)</label>
              <input
                type="text"
                value={whatsappModal.phone}
                onChange={(e) => setWhatsappModal((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                placeholder="9876543210"
                maxLength={10}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #e9edef", borderRadius: "6px", fontSize: "16px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button type="button" onClick={closeWhatsappModal} style={{ padding: "10px 20px", border: "1px solid #ddd", borderRadius: "8px", background: "#fff", cursor: "pointer" }}>Cancel</button>
              <button
                type="button"
                onClick={handleSendWhatsAppBill}
                disabled={sendingWhatsApp || whatsappModal.phone.replace(/\D/g, "").length !== 10}
                style={{ padding: "10px 20px", border: "none", borderRadius: "8px", background: "#25d366", color: "#fff", cursor: "pointer" }}
              >
                {sendingWhatsApp ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Creation Modal - opens directly without redirect */}
      {billModalOpen && (
        <BillCreationModal
          open={billModalOpen}
          onClose={() => {
            setBillModalOpen(false);
            setBillModalPartyId(null);
            setBillModalEditBillId(null);
            if (selectedParty?._id) billService.getAllBills({ partyId: selectedParty._id }).then((res) => setPartyBills(res.bills || [])).catch(() => setPartyBills([]));
            hasLoadedParties.current = false;
            reloadPartyList();
            hasLoadedParties.current = true;
          }}
          preselectedPartyId={billModalPartyId}
          editBillId={billModalEditBillId}
        />
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseModal}
        >
          <div 
            style={{
              ...styles.modalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "800px",
              width: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
              marginBottom: isMobile ? "100px" : "0",
              display: "flex",
              flexDirection: "column",
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "24px",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>
                {editingParty ? "Edit Party Details" : "Add Party Details"}
              </h3>
              <button 
                onClick={handleCloseModal} 
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  GST Number <span style={styles.optional}>(Optional)</span>
                </label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    type="text"
                    value={formData.gstin}
                    onChange={(e) => {
                      setValidationErrors((prev) => ({ ...prev, gstin: "" }));
                      setFormData({
                        ...formData,
                        gstin: e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, ""),
                      });
                    }}
                    onBlur={handleGstinLookup}
                    maxLength="15"
                    disabled={gstLookupLoading}
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.gstin ? "#dc3545" : "#e9edef",
                      flex: "1 1 200px",
                      minWidth: 0,
                    }}
                    placeholder="Enter 15-character GST number"
                    autoComplete="off"
                  />
                  {gstLookupLoading && (
                    <span
                      className="party-gst-spinner"
                      role="status"
                      aria-label="Loading GST details"
                    />
                  )}
                </div>
                {validationErrors.gstin && (
                  <span style={styles.error}>{validationErrors.gstin}</span>
                )}
              </div>

              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Company Name <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => {
                      gstUserEditedRef.current.companyName = true;
                      setFormData({ ...formData, companyName: e.target.value });
                    }}
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.companyName
                        ? "#dc3545"
                        : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter company name"
                  />
                  {validationErrors.companyName && (
                    <span style={styles.error}>
                      {validationErrors.companyName}
                    </span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Party Name <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.partyName}
                    onChange={(e) => {
                      gstUserEditedRef.current.partyName = true;
                      setFormData({ ...formData, partyName: e.target.value });
                    }}
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.partyName ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter party name"
                  />
                  {validationErrors.partyName && (
                    <span style={styles.error}>{validationErrors.partyName}</span>
                  )}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Phone number <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    setValidationErrors((prev) => ({ ...prev, mobileNumber: "" }));
                    setFormData({
                      ...formData,
                      mobileNumber: e.target.value.replace(/\D/g, "").slice(0, 10),
                    });
                  }}
                  maxLength={10}
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.mobileNumber ? "#dc3545" : "#e9edef",
                    width: "100%",
                    maxWidth: isMobile ? "100%" : "280px",
                  }}
                  placeholder="10-digit mobile (not required; duplicates allowed)"
                  autoComplete="tel"
                />
                {validationErrors.mobileNumber && (
                  <span style={styles.error}>{validationErrors.mobileNumber}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Address <span style={styles.optional}>(Optional)</span>
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => {
                    gstUserEditedRef.current.address = true;
                    setFormData({ ...formData, address: e.target.value });
                  }}
                  style={{
                    ...styles.textarea,
                    borderColor: validationErrors.address ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter complete address"
                  rows="3"
                />
                {validationErrors.address && (
                  <span style={styles.error}>{validationErrors.address}</span>
                )}
              </div>

              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Aadhar Number <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.aadhar}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        aadhar: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    maxLength="12"
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.aadhar ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter 12-digit Aadhar"
                  />
                  {validationErrors.aadhar && (
                    <span style={styles.error}>{validationErrors.aadhar}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    PAN Number <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.pan}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                      })
                    }
                    maxLength="10"
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.pan ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter PAN (e.g., ABCDE1234F)"
                  />
                  {validationErrors.pan && (
                    <span style={styles.error}>{validationErrors.pan}</span>
                  )}
                </div>
              </div>

              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    State <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({ ...formData, state: e.target.value })
                    }
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.state ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter state"
                  />
                  {validationErrors.state && (
                    <span style={styles.error}>{validationErrors.state}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    City <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.city ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter city"
                  />
                  {validationErrors.city && (
                    <span style={styles.error}>{validationErrors.city}</span>
                  )}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Party Type</label>
                <select
                  value={formData.partyType || "Customer"}
                  onChange={(e) => setFormData({ ...formData, partyType: e.target.value })}
                  style={{
                    ...styles.input,
                    width: "100%",
                  }}
                >
                  <option value="Vyapari">Vyapari</option>
                  <option value="Customer">Customer</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    style={styles.checkbox}
                  />
                  Active
                </label>
              </div>

              <div style={{
                ...styles.formActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    ...styles.cancelButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "100px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "140px",
                  }}
                >
                  {createLoading || updateLoading
                    ? "Saving..."
                    : editingParty
                    ? "Update Party"
                    : "Save Party"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseDeleteModal}
        >
          <div
            style={{
              ...styles.deleteModalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "24px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "400px",
              width: isMobile ? "100%" : "90%",
              marginBottom: isMobile ? "100px" : "0",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              ...styles.deleteModalTitle,
              fontSize: isMobile ? "18px" : "20px",
            }}>Confirm Delete</h3>
            <p style={styles.deleteModalText}>
              Are you sure you want to delete this customer? This action cannot be
              undone.
            </p>
            <div style={{
              ...styles.deleteModalActions,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...styles.cancelButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                style={{
                  ...styles.deleteConfirmButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNavbar />
    </div>
  );
};

const billMenuBtnStyle = {
  display: "block",
  width: "100%",
  padding: "10px 14px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  fontSize: "14px",
  cursor: "pointer",
  color: "#111b21",
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  pageHeader: {
    marginBottom: "24px",
    paddingBottom: "16px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  filterChip: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    border: "1px solid #e9edef",
    borderRadius: 20,
    backgroundColor: "#fff",
    color: "#667781",
    cursor: "pointer",
  },
  filterChipActive: {
    backgroundColor: "#25d366",
    color: "#fff",
    borderColor: "#25d366",
  },
  stateSelect: {
    flex: 1,
    minWidth: 140,
    maxWidth: 220,
    padding: "8px 12px",
    fontSize: 14,
    border: "1px solid #e9edef",
    borderRadius: 8,
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  clearFiltersBtn: {
    padding: "6px 12px",
    fontSize: 12,
    border: "1px solid #e9edef",
    borderRadius: 6,
    background: "#f0f2f5",
    color: "#111b21",
    cursor: "pointer",
  },
  searchContainer: {
    marginBottom: "20px",
    padding: "12px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    border: "1px solid #e9edef",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    outline: "none",
    transition: "border-color 0.2s",
    letterSpacing: "-0.01em",
  },
  partiesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "20px",
  },
  partyCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #ffd6e0",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  partyCardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  partyInfo: {
    flex: 1,
    minWidth: 0,
  },
  partyName: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "8px",
    letterSpacing: "-0.01em",
    lineHeight: "22px",
    textTransform: "uppercase",
  },
  partyDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  partyDetailRow: {
    fontSize: "14px",
    fontWeight: "400",
    color: "#667781",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  partyDetailLabel: {
    fontWeight: "500",
    color: "#667781",
  },
  partyDetailValue: {
    color: "#111b21",
  },
  separator: {
    color: "#667781",
    margin: "0 4px",
  },
  partyActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  partyEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  partyDeleteButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  noParties: {
    padding: "80px 40px",
    textAlign: "center",
    color: "#667781",
  },
  createFirstButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
  },
  fabButton: {
    position: "fixed",
    bottom: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "32px",
    padding: "16px 32px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 16px rgba(220, 53, 69, 0.4)",
    zIndex: 1001,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fabIcon: {
    fontSize: "24px",
    lineHeight: "1",
  },
  fabText: {
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  pageInfo: {
    fontSize: "16px",
    color: "#333",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    justifyContent: "center",
    zIndex: 1002,
    overflowY: "auto",
    paddingBottom: "100px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "800px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e9edef",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "32px",
    cursor: "pointer",
    color: "#667781",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, color 0.2s",
    lineHeight: "1",
    fontWeight: "300",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  optional: {
    color: "#6c757d",
    fontSize: "12px",
    fontWeight: "normal",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  textarea: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    resize: "vertical",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
  },
  error: {
    color: "#dc3545",
    fontSize: "12px",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
    borderTop: "1px solid #e9edef",
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 1,
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "400px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  deleteModalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "12px",
    letterSpacing: "-0.01em",
  },
  deleteModalText: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    lineHeight: "20px",
    fontWeight: "400",
  },
  deleteModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
  },
  deleteConfirmButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};

export default PartyManagement;

